// components/payment/PaywallSheet.tsx
// Token overlay that hosts PaywallPanel in-context (e.g. a free member taps a locked
// stage). Bottom-sheet on mobile, centered card on desktop. Border-not-shadow, canvas bg
// (never a raised surface), Esc / backdrop / scroll-lock, dir-aware.

import React, { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import PaywallPanel, { type PaywallReason } from './PaywallPanel'
import { useI18n } from '../../i18n/I18nContext'

interface PaywallSheetProps {
    onClose: () => void
    classId?: string
    stageName?: string
    reason?: PaywallReason
}

const PaywallSheet: React.FC<PaywallSheetProps> = ({ onClose, classId, stageName, reason }) => {
    const { t, language } = useI18n()
    const panelRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)
    const headingId = useId()
    const [processing, setProcessing] = useState(false)
    const processingRef = useRef(false)
    const onCloseRef = useRef(onClose)
    useEffect(() => { onCloseRef.current = onClose }, [onClose])
    useEffect(() => { processingRef.current = processing }, [processing])

    useEffect(() => {
        const prevOverflow = document.body.style.overflow
        const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const app = document.getElementById('root')
        const previousInert = app?.inert ?? false
        if (app) app.inert = true
        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()
        const onKey = (e: KeyboardEvent) => {
            // Paystack appends its own popup outside this portal. Let it own its keys.
            if (processingRef.current && e.target instanceof Node && !panelRef.current?.contains(e.target)) return
            if (e.key === 'Escape') {
                e.preventDefault()
                onCloseRef.current()
            }
            if (e.key === 'Tab') {
                const controls = panelRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')
                const first = controls?.[0]
                const last = controls?.[controls.length - 1]
                if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus() }
                else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus() }
            }
        }
        const containFocus = (e: FocusEvent) => {
            if (!processingRef.current && e.target instanceof Node && !panelRef.current?.contains(e.target)) closeRef.current?.focus()
        }
        document.addEventListener('keydown', onKey)
        document.addEventListener('focusin', containFocus)
        return () => {
            document.body.style.overflow = prevOverflow
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('focusin', containFocus)
            if (app) app.inert = previousInert
            if (previousFocus?.isConnected) previousFocus.focus()
        }
    }, [])

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4"
            onClick={e => {
                if (e.target === e.currentTarget) onClose()
            }}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={headingId}
                tabIndex={-1}
                className="relative max-h-[92dvh] w-full overflow-y-auto overscroll-contain border-2 border-t-0 sm:border-t-2 border-[var(--dhtn-border)] bg-[var(--dhtn-canvas)] p-5 outline-none rounded-t-[var(--dhtn-radius-card)] sm:max-w-md sm:rounded-card sm:p-8"
            >
                <button
                    type="button"
                    ref={closeRef}
                    onClick={onClose}
                    aria-label={t('paywall.close')}
                    className="absolute top-3 end-3 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-control text-[var(--dhtn-text-muted)] hover:text-[var(--dhtn-text)] disabled:cursor-wait focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dhtn-text)]"
                >
                    <X size={20} aria-hidden="true" />
                </button>

                <PaywallPanel mode="sheet" classId={classId} stageName={stageName} reason={reason} onClose={onClose} headingId={headingId} onProcessingChange={setProcessing} />
            </div>
        </div>, document.body
    )
}

export default PaywallSheet
